import { useNavigate } from 'react-router-dom';

import { useMyTimesheets, useSubmitAllTimesheets } from '@frontend/api/hooks/timesheets';
import type { Timesheet } from '@shared/types';
import { formatDateRange, formatHours, todayISO } from '@frontend/lib/format';
import { Button } from '@frontend/components/ui/Button';
import { DataTable, type Column } from '@frontend/components/ui/DataTable';
import { PageHeader } from '@frontend/components/ui/Page';
import { StatusBadge } from '@frontend/components/ui/StatusBadge';
import { EmptyState, ErrorState, LoadingRows } from '@frontend/components/ui/States';
import { useToast } from '@frontend/components/ui/Toast';

export function TimesheetHistoryPage(): JSX.Element {
  const query = useMyTimesheets();
  const navigate = useNavigate();
  const submitAll = useSubmitAllTimesheets();
  const toast = useToast();

  /**
   * Somebody back from three weeks of leave has three sheets to submit and no reason to
   * open each one. The count is only for the LABEL — which sheets actually qualify is the
   * server's decision, because a client-side filter that disagrees with it would submit
   * nothing and report success.
   */
  const today = todayISO();
  const outstanding = (query.data?.timesheets ?? []).filter(
    (row) => (row.status === 'DRAFT' || row.status === 'REJECTED') && row.periodEnd < today,
  ).length;

  const columns: Column<Timesheet>[] = [
    {
      key: 'period',
      header: 'Period',
      cell: (row) => (
        <span className="font-medium text-content">{formatDateRange(row.periodStart, row.periodEnd)}</span>
      ),
      sort: (a, b) => a.periodStart.localeCompare(b.periodStart),
    },
    { key: 'status', header: 'Status', cell: (row) => <StatusBadge status={row.status} /> },
    { key: 'total', header: 'Total', numeric: true, cell: (row) => formatHours(row.totalHours) },
    {
      key: 'billable',
      header: 'Billable',
      numeric: true,
      hideOnMobile: true,
      cell: (row) => formatHours(row.billableHours),
    },
    {
      key: 'overtime',
      header: 'Overtime',
      numeric: true,
      hideOnMobile: true,
      cell: (row) =>
        row.isOvertime ? (
          <span className="font-medium text-warning">{formatHours(row.overtimeHours)}</span>
        ) : (
          <span className="text-content-tertiary">—</span>
        ),
    },
    {
      key: 'pto',
      header: 'Paid time off',
      numeric: true,
      hideOnMobile: true,
      cell: (row) => formatHours(row.paidTimeOffHours),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Timesheet history"
        description="Every period you have opened, newest first."
        actions={
          outstanding > 0 ? (
            <Button
              variant="primary"
              data-testid="submit-all-timesheets"
              isLoading={submitAll.isPending}
              onClick={() => {
                submitAll.mutate(undefined, {
                  onSuccess: (result) => {
                    const sent = result.submitted?.length ?? 0;
                    const held = result.skipped ?? [];
                    // Both halves, always. "3 submitted" while two were silently held back
                    // is how somebody discovers in the payroll meeting that a week never
                    // went in.
                    toast.success(
                      held.length === 0
                        ? `${sent} timesheet${sent === 1 ? '' : 's'} submitted.`
                        : `${sent} submitted. ${held.length} could not be: ${held[0]?.reason ?? 'see the period'}`,
                    );
                  },
                  onError: (error) => toast.fromError(error, 'Could not submit your outstanding timesheets'),
                });
              }}
            >
              Submit {outstanding} outstanding
            </Button>
          ) : undefined
        }
      />
      {query.isLoading ? (
        <LoadingRows rows={8} columns={6} />
      ) : query.isError ? (
        <ErrorState
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : (
        <div className="surface-card">
          <DataTable
            rows={query.data?.timesheets ?? []}
            columns={columns}
            rowKey={(row) => row.id}
            testIdPrefix="timesheet-row"
            onRowClick={(row) => navigate(`/timesheets?date=${row.periodStart}`)}
            emptyState={
              <EmptyState
                title="No timesheets yet"
                description="Your first timesheet is created the moment you open the current period. Go to My timesheet to start logging hours."
              />
            }
          />
        </div>
      )}
    </div>
  );
}
